import React from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'

type Props = {
  title: React.ReactNode
  subTitle?: string
  variant?: 'primary' | 'secondary'
  onClick?: () => void
  rightAlignedComponent?: React.ReactNode
  hasBoxShadow?: boolean
}

export const PageHeader = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      subTitle,
      variant = 'secondary',
      onClick,
      rightAlignedComponent,
      hasBoxShadow = false,
    },
    ref
  ) => {
    return (
      <HeaderStyled
        ref={ref}
        data-testid='PageHeader'
        data-variant={variant}
        data-has-on-click={!!onClick}
        data-box-shadow={hasBoxShadow}
      >
        <div onClick={onClick}>
          <h1>
            {title}
            {onClick && <Icon name='SolidChevronDown' color='currentColor' size='2.4rem' />}
          </h1>
          {subTitle && <p>{subTitle}</p>}
        </div>

        {rightAlignedComponent ? <div>{rightAlignedComponent}</div> : null}
      </HeaderStyled>
    )
  }
)

PageHeader.displayName = 'PageHeader'

// ======
// Styles
// ======

const HeaderStyled = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacing.smx2,
  boxSizing: 'border-box',
  minHeight: '5.2rem',
  padding: `${spacing.smx1} ${spacing.md}`,

  background: colors.lavender[750],
  color: colors.white,

  '> div:first-of-type': {
    fontFamily: font.family.primary,

    '> *': { margin: 0, padding: 0 },

    '> h1': {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx2,
      textTransform: 'unset',
      textAlign: 'left',
      background: 'unset',
      color: colors.white,
    },

    '> p': {
      color: colors.lavender[100],
      textAlign: 'left',
      fontWeight: font.weight.medium,
      fontSize: font.size.lg.fontSize,
      lineHeight: font.size.lg.lineHeight,
      letterSpacing: font.size.lg.letterSpacing,
    },
  },

  '> div:nth-of-type(2)': { flexShrink: 0 },

  '> a, > button': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  '> a': {
    textDecoration: 'none',
    color: 'unset',
    userSelect: 'none',
    transition: 'opacity 0.25s ease-out',
    WebkitTapHighlightColor: 'transparent',

    '@media (hover: hover) and (pointer: fine)': { ':hover': { opacity: 0.75 } },
    ':active': { opacity: 0.5 },
  },

  '&[data-variant="primary"]': {
    h1: {
      fontWeight: font.weight.bold,
      fontSize: font.size.xl4.fontSize,
      lineHeight: font.size.xl4.lineHeight,
      letterSpacing: font.size.xl4.letterSpacing,
    },
  },

  '&[data-variant="secondary"]': {
    h1: {
      fontWeight: font.weight.semibold,
      fontSize: font.size.xl2.fontSize,
      lineHeight: font.size.xl2.lineHeight,
      letterSpacing: font.size.xl2.letterSpacing,
    },
  },

  '&[data-box-shadow="true"]': {
    boxShadow: shadow.md,
  },

  '&[data-has-on-click="true"] > div:first-of-type': {
    position: 'relative',
    background: colors.lavender[750],
    color: colors.white,
    border: 'none',
    borderRadius: radius.lgx1,
    fontFamily: font.family.primary,
    fontWeight: font.weight.semibold,
    fontSize: font.size.xl2.fontSize,
    letterSpacing: font.size.xl2.letterSpacing,
    lineHeight: font.size.xl2.lineHeight,
    transition: 'all 0.25s ease',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',

    padding: spacing.smx2,
    paddingLeft: spacing.smx1,
    marginLeft: `-${spacing.smx1}`,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': { background: colors.lavender[800] },
    },

    '&:active': { transform: 'scale(0.95)', opacity: 0.75, background: colors.lavender[800] },
    '&:focus-visible': { outline: `0.2rem solid ${colors.white}`, outlineOffset: '0.2rem' },
  },
})
